"use client";

import type React from "react";
import { useState } from "react";
import {
  FormControl,
  FormLabel,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  HStack,
  Box,
} from "@chakra-ui/react";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface InterruptionSensitivitySliderProps {
  value: number | undefined;
  onChange: (value: number) => Promise<boolean> | void;
}

export const InterruptionSensitivitySlider: React.FC<
  InterruptionSensitivitySliderProps
> = ({ value = 1, onChange }) => {
  const [localValue, setLocalValue] = useState(value);
  const [isUpdating, setIsUpdating] = useState(false);
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const handleChange = (newValue: number) => {
    setLocalValue(newValue);
  };

  const handleChangeEnd = async (newValue: number) => {
    if (newValue === value) return;
    setIsUpdating(true);
    try {
      await onChange(newValue);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <FormControl>
      <FormLabel
        fontSize="sm"
        fontWeight="medium"
        color={isDark ? "gray.300" : "gray.700"}
        mb={1}
      >
        Interruption Sensitivity
      </FormLabel>
      <Text fontSize="xs" color={isDark ? "gray.400" : "gray.600"} mb={2}>
        Control how sensitively AI can be interrupted by human speech.
      </Text>
      <HStack spacing={4}>
        <Slider
          aria-label="Interruption Sensitivity"
          min={0}
          max={1}
          step={0.01}
          value={localValue}
          onChange={handleChange}
          onChangeEnd={handleChangeEnd}
          isDisabled={isUpdating}
          flex={1}
        >
          <SliderTrack bg={isDark ? "gray.600" : "gray.200"}>
            <SliderFilledTrack bg="gray.500" />
          </SliderTrack>
          <SliderThumb boxSize={4} />
        </Slider>
        <Box minW="20px" textAlign="center">
          <Text
            fontSize="sm"
            fontWeight="medium"
            color={isDark ? "gray.300" : "gray.700"}
          >
            {localValue.toFixed(2)}
          </Text>
        </Box>
      </HStack>
    </FormControl>
  );
};
